"use client"

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound, Mail, Save, Send, ShieldCheck, UserRound } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { requestOwnPasswordReset, updateMe, type Me } from '@/services/user'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  me: Me
}

function cleanName(value: string) {
  return value.replace(/[^A-Za-z]/g, '').slice(0, 15)
}

export function ClientProfileDialog({ open, onOpenChange, me }: Props) {
  const queryClient = useQueryClient()
  const [firstName, setFirstName] = React.useState(me.first_name || '')
  const [lastName, setLastName] = React.useState(me.last_name || '')
  const [saving, setSaving] = React.useState(false)
  const [sendingReset, setSendingReset] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setFirstName(cleanName(me.first_name || ''))
    setLastName(cleanName(me.last_name || ''))
  }, [open, me.first_name, me.last_name])

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    try {
      const updated = await updateMe({
        first_name: firstName,
        last_name: lastName,
      })
      queryClient.setQueryData(['me'], updated)
      toast.success('Profile updated')
      onOpenChange(false)
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Unable to update profile'
      toast.error(detail)
    } finally {
      setSaving(false)
    }
  }

  const onRequestPasswordReset = async () => {
    setSendingReset(true)
    try {
      await requestOwnPasswordReset()
      toast.success('Password reset email sent')
      onOpenChange(false)
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Unable to send password reset email'
      toast.error(detail)
    } finally {
      setSendingReset(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-0 bg-white p-0 shadow-2xl shadow-slate-950/25 sm:max-w-[560px]">
        <div className="relative bg-[#0F172A] px-6 pb-7 pt-6 text-white">
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#60A5FA] to-transparent" />
          <DialogHeader className="pr-8">
            <div className="mb-4 flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-[#334155] bg-[#1E293B] text-[#93C5FD] shadow-inner">
                <UserRound className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-2xl font-extrabold leading-8 tracking-normal text-white">
                  Profile
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm font-medium leading-5 text-slate-300">
                  Manage your client portal identity and password access.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <form onSubmit={onSave} className="space-y-5 bg-[#F8FAFC] px-6 pb-6 pt-5">
          <div className="border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-extrabold uppercase leading-4 tracking-[0.12em] text-slate-500">
              <ShieldCheck className="h-4 w-4 text-[#2563EB]" />
              Account Details
            </div>

            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="client-profile-email" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Email</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="client-profile-email"
                    value={me.email}
                    readOnly
                    className="h-11 rounded-md border-slate-200 bg-slate-50 pl-9 text-sm font-semibold text-slate-600 shadow-none"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="client-profile-first-name" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">First name</Label>
                  <Input
                    id="client-profile-first-name"
                    value={firstName}
                    maxLength={15}
                    onChange={(event) => setFirstName(cleanName(event.target.value))}
                    autoComplete="given-name"
                    className="h-11 rounded-md border-slate-200 bg-white text-sm font-semibold shadow-none focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="client-profile-last-name" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Last name</Label>
                  <Input
                    id="client-profile-last-name"
                    value={lastName}
                    maxLength={15}
                    onChange={(event) => setLastName(cleanName(event.target.value))}
                    autoComplete="family-name"
                    className="h-11 rounded-md border-slate-200 bg-white text-sm font-semibold shadow-none focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-4 shadow-sm">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#DBEAFE] text-[#1D4ED8]">
                <KeyRound className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-extrabold leading-5 text-slate-950">Password access</div>
                <div className="mt-1 text-sm leading-5 text-slate-600">
                  Send a time-limited reset link to your registered email address.
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 h-10 rounded-md border-[#93C5FD] bg-white px-4 text-sm font-extrabold text-[#1D4ED8] shadow-none hover:bg-[#DBEAFE]"
                  onClick={onRequestPasswordReset}
                  disabled={sendingReset}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {sendingReset ? 'Sending...' : 'Change password'}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-200 pt-5">
            <Button type="button" variant="outline" className="h-10 rounded-md border-slate-300 bg-white px-4 text-sm font-bold shadow-none" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="h-10 rounded-md bg-[#2563EB] px-4 text-sm font-extrabold text-white shadow-none hover:bg-[#1D4ED8]" disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
